/* eslint-disable */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';

// Lightweight count queries for the Welcome screen portal cards.
//
// `head:true` is used wherever possible so only count metadata returns.
// Brands / designers / items-with-sales cannot use head:true — PostgREST
// has no count(distinct …) shortcut for JSONB-nested or row values, so we
// fetch only the column needed and Set-dedupe client-side (mirrors the
// pattern in useDerivedData.js:42).
//
// TOTAL DEVS uses ONLY `.is('deleted_at', null)` — no archived filter.
// Per Phase 1 discovery (2026-04-26): the prior `.neq('data->>archived',
// 'true')` formulation excluded NULL rows under PostgREST three-valued
// logic, returning ~104 instead of the real ~2,956. Other call sites that
// use the same .neq() pattern (e.g. useDevsPaginated.js:19) likely have
// the same bug and should be audited in a future brief.
//
// Designer normalization mirrors src/components/developments/analytics/
// sections/people/designerDisplay.js — all-caps names like "YAELI" are
// collapsed to "Yaeli" so the dedupe count matches what the user sees in
// the Analytics People view.
function normalizeDesigner(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.length > 1 && s === s.toUpperCase()) {
    return s.charAt(0) + s.slice(1).toLowerCase();
  }
  return s;
}

function formatBatchPeriod(from, to) {
  if (!from || !to) return null;
  const f = new Date(from);
  const t = new Date(to);
  if (isNaN(f.getTime()) || isNaN(t.getTime())) return null;
  const fM = f.toLocaleString('en', { month: 'short' });
  const tM = t.toLocaleString('en', { month: 'short' });
  const fY = f.getFullYear();
  const tY = t.getFullYear();
  if (fY === tY && fM === tM) return `${fM} ${fY}`;
  if (fY === tY) return `${fM} – ${tM} ${tY}`;
  return `${fM} ${fY} – ${tM} ${tY}`;
}

export function usePortalCounts(enabled = true) {
  return useQuery({
    queryKey: ['portal-counts'],
    queryFn: async () => {
      const [
        itemsTotal,
        brandCountsRes,
        devsTotal,
        devsDesignerRows,
        qcReports,
        qcAwaiting,
        qcFactories,
        salesItemsRes,
        salesLatestBatchRow,
      ] = await Promise.all([
        supabase.from('items').select('*', { count: 'exact', head: true }),
        // Active brands — authoritative, uncapped (the Brand Manager's source).
        // get_brand_counts returns per-brand {brand, count}; "in use" = count > 0.
        // Replaces the prior data->brand .select()+Set dedupe, which the ~1000-row
        // cap undercounted (192 vs ~964). Models dropped (#3c): distinct(model) ==
        // SKU count, so there is no real model-family metric.
        supabase.rpc('get_brand_counts'),
        supabase
          .from('developments')
          .select('*', { count: 'exact', head: true })
          .is('deleted_at', null),
        supabase
          .from('developments')
          .select('data->designer')
          .is('deleted_at', null),
        supabase.from('qc_reports').select('*', { count: 'exact', head: true }),
        // Awaiting QC — mirrors the Reports Queue badge (QcPortal.js:57 /
        // QcReportsQueue.js:40): status IN ('submitted','pending'). 'submitted'
        // = "PENDING REVIEW" per QC_STATUS; 'pending' is the legacy alias.
        supabase
          .from('qc_reports')
          .select('*', { count: 'exact', head: true })
          .in('status', ['submitted', 'pending']),
        // Factories — active QC suppliers (the set QcNewStep1.js:36 uses).
        supabase
          .from('qc_suppliers')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true),
        // Items with sales — uncapped server-side distinct count (RPC). Replaces
        // the prior sales_lines .select('barcode')+Set dedupe (also ~1000-capped).
        supabase.rpc('get_sales_items_with_sales'),
        supabase
          .from('sales_days')   // renamed from sales_batches in WS-1
          .select('period_from,period_to')
          .order('period_from', { ascending: false })
          .limit(1),
      ]);

      const all = [
        itemsTotal, brandCountsRes, devsTotal, devsDesignerRows,
        qcReports, qcAwaiting, qcFactories, salesItemsRes, salesLatestBatchRow,
      ];
      const firstError = all.find(r => r.error);
      if (firstError?.error) throw firstError.error;

      // Active brands "in use" — brands with >= 1 item. get_brand_counts is the
      // Brand Manager's authoritative, uncapped source (it returns per-brand
      // {brand, count}); ~964.
      const brandsInUse = (brandCountsRes.data || [])
        .filter(r => Number(r.count) > 0)
        .length;

      const designersCount = new Set(
        (devsDesignerRows.data || [])
          .map(r => normalizeDesigner(r.designer))
          .filter(Boolean)
      ).size;

      // Items with sales — RPC returns a single int (uncapped distinct barcode > 0).
      const salesItemsWithSales = typeof salesItemsRes.data === 'number'
        ? salesItemsRes.data
        : 0;

      const latestBatch = salesLatestBatchRow.data?.[0];
      const salesLatestBatch = latestBatch
        ? formatBatchPeriod(latestBatch.period_from, latestBatch.period_to)
        : null;

      return {
        itemsTotal: itemsTotal.count ?? 0,
        itemsBrands: brandsInUse,
        devsTotal: devsTotal.count ?? 0,
        devsDesigners: designersCount,
        qcReports: qcReports.count ?? 0,
        qcAwaiting: qcAwaiting.count ?? 0,
        qcFactories: qcFactories.count ?? 0,
        salesItemsWithSales,
        salesLatestBatch, // string (e.g. "Jan – Apr 2026") or null
      };
    },
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
